import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp, DollarSign, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#C49FFF', '#FFD700'];

export default function Analyse() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeframe, setTimeframe] = useState("quarter");
  const [activeTab, setActiveTab] = useState("overview");
  const [punkteTab, setPunkteTab] = useState("gesamt");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [timeframe]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/analytics?timeframe=${timeframe}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Analysedaten");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analyticsData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-lg text-slate-600">Lade Analysedaten...</div>
      </div>
    );
  }

  const top3 = analyticsData.top_articles.slice(0, 3);
  const top10 = analyticsData.top_articles.slice(0, 10);

  // Prepare period data for charts
  const periodChartData = Object.entries(analyticsData.period_data).map(([period, data]) => ({
    period,
    zugaenge: data.zugaenge || 0,
    abgaenge: data.abgaenge || 0,
    umsatz: data.umsatz || 0,
    gewinn: data.gewinn || 0,
    punkte: data.punkte || 0
  })).sort((a, b) => a.period.localeCompare(b.period));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Fixed Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zu Inventory
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-slate-800">Analyse</h1>
          </div>
          
          {/* Timeframe Filter - Now below title */}
          <div className="flex gap-2 mt-4">
            <Button 
              size="sm"
              variant={timeframe === "quarter" ? "default" : "outline"}
              onClick={() => setTimeframe("quarter")}
            >
              Aktuelles Quartal
            </Button>
            <Button 
              size="sm"
              variant={timeframe === "year" ? "default" : "outline"}
              onClick={() => setTimeframe("year")}
            >
              Jahr 2025
            </Button>
            <Button 
              size="sm"
              variant={timeframe === "all" ? "default" : "outline"}
              onClick={() => setTimeframe("all")}
            >
              Gesamte Zeit
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-slate-200">
            <button
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === "overview" 
                  ? "text-emerald-600 border-b-2 border-emerald-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              Übersicht
            </button>
            <button
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === "revenue" 
                  ? "text-emerald-600 border-b-2 border-emerald-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setActiveTab("revenue")}
            >
              Umsatz & Gewinn
            </button>
            <button
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === "stock" 
                  ? "text-emerald-600 border-b-2 border-emerald-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setActiveTab("stock")}
            >
              Bestand
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Total Points Card with Tabs */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg p-6 shadow-lg text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <p className="text-emerald-100 text-sm font-medium mb-3">
                    Gesammelte Punkte ({timeframe === 'quarter' ? 'Quartal' : timeframe === 'year' ? 'Jahr' : 'Gesamt'})
                  </p>
                  
                  {/* Mini Tabs */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setPunkteTab("geschäftlich")}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        punkteTab === "geschäftlich"
                          ? "bg-white text-emerald-600"
                          : "bg-white bg-opacity-20 hover:bg-opacity-30"
                      }`}
                    >
                      Gesch.
                    </button>
                    <button
                      onClick={() => setPunkteTab("haushalt")}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        punkteTab === "haushalt"
                          ? "bg-white text-emerald-600"
                          : "bg-white bg-opacity-20 hover:bg-opacity-30"
                      }`}
                    >
                      Haush.
                    </button>
                    <button
                      onClick={() => setPunkteTab("gesamt")}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        punkteTab === "gesamt"
                          ? "bg-white text-emerald-600"
                          : "bg-white bg-opacity-20 hover:bg-opacity-30"
                      }`}
                    >
                      Gesamt
                    </button>
                  </div>

                  <p className="text-4xl font-bold">
                    {punkteTab === "geschäftlich" 
                      ? Object.values(analyticsData.period_data).reduce((sum, data) => sum + (data.punkte_geschäftlich || 0), 0).toFixed(2)
                      : punkteTab === "haushalt"
                      ? Object.values(analyticsData.period_data).reduce((sum, data) => sum + (data.punkte_haushalt || 0), 0).toFixed(2)
                      : Object.values(analyticsData.period_data).reduce((sum, data) => sum + (data.punkte || 0), 0).toFixed(2)
                    }
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-4 rounded-full">
                  <TrendingUp className="w-10 h-10" />
                </div>
              </div>
            </div>

            {/* Top 3 Cards */}
            <div>
              <h2 className="text-xl font-semibold text-slate-800 mb-4">Top 3 Verkaufsartikel</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {top3.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 shadow-md border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl font-bold text-emerald-600">#{index + 1}</span>
                      <TrendingUp className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-1">{item.article}</h3>
                    <div className="text-sm text-slate-600">
                      <div>Verkauft: <span className="font-semibold">{item.quantity} Stk</span></div>
                      <div>Umsatz: <span className="font-semibold">{item.revenue.toFixed(2)} €</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pie Chart */}
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Top 3 Verteilung (Menge)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={top3}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.article}: ${entry.quantity}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="quantity"
                  >
                    {top3.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legende */}
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-slate-600 mb-2">Legende:</h4>
                {top3.map((entry, index) => (
                  <div key={`legend-${index}`} className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className="text-sm text-slate-700">
                      {entry.article} ({entry.quantity} Stück)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 10 Bar Chart */}
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Top 10 Meistverkaufte Artikel</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={top10}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="article" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="quantity" fill="#10b981" name="Verkaufte Menge" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Revenue & Profit Tab */}
        {activeTab === "revenue" && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Umsatzentwicklung</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={periodChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="umsatz" stroke="#3b82f6" strokeWidth={2} name="Umsatz (€)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Gewinnentwicklung</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={periodChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="gewinn" stroke="#10b981" strokeWidth={2} name="Gewinn (€)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Stock Tab */}
        {activeTab === "stock" && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Zugänge vs Abgänge (Menge)</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={periodChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="zugaenge" fill="#3b82f6" name="Zugänge" />
                  <Bar dataKey="abgaenge" fill="#ef4444" name="Abgänge" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Punkte-Entwicklung</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={periodChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="punkte" stroke="#10b981" strokeWidth={3} name="Punkte" dot={{ fill: '#10b981', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
